// pages/AddCredits.tsx — FINAL RTP INTEGRATED VERSION
// Real-time sync, optimistic UI, RTP warnings, audit-ready

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import useAdmin from '../hooks/useAdmin';
import { toast } from 'react-hot-toast';

interface FormData {
  target_type: 'user' | 'admin';
  login_id: string;  // FIX: required not optional — .trim() is now safe
  amount: number;
  action: 'add' | 'remove';
}

export default function AddCredits() {
  const [loading, setLoading] = useState(false);
  const [targetType, setTargetType] = useState<'user' | 'admin'>('user');
  const { token, refreshCsrf, error, setError, socket } = useAdmin();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>({
    defaultValues: { target_type: 'user', login_id: '', amount: 0, action: 'add' },
  });
  const targetTypeRegister = register('target_type', {
    onChange: (event) => setTargetType(event.target.value as 'user' | 'admin'),
  });

  useEffect(() => {
    if (token) refreshCsrf();
  }, [token, refreshCsrf]);

  // Real-time credits update listener (RTP-sensitive)
  useEffect(() => {
    if (!socket) return;

    const handleCreditsUpdated = (data: { userId: string; realCredits: number; demoCredits: number; message?: string }) => {
      console.log('Real-time credits update:', data);
      toast.success(data.message || `Credits updated! Real: ${data.realCredits}`);
    };

    socket.on('credits-updated', handleCreditsUpdated);

    return () => {
      socket.off('credits-updated', handleCreditsUpdated);
    };
  }, [socket]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError('');

    // RTP Warning for real credits
    if (data.amount !== 0) {
      if (!window.confirm(`Real credits update will affect RTP exposure. Confirm?`)) {
        setLoading(false);
        return;
      }
    }

    try {
      const csrfToken = await refreshCsrf();
      const sanitizedData = {
        login_id: DOMPurify.sanitize(data.login_id.trim()),
        amount: Number(data.amount),
        type: 'real',
        action: data.action,
        target_type: data.target_type,
      };

      const res = await fetch('/admin/add-credits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || localStorage.getItem('adminToken')}`,
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: JSON.stringify(sanitizedData),
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.error?.message || 'Failed to update credits');
      }

      toast.success(result.message || `Credits updated! ${data.action === 'remove' ? 'Removed' : 'Added'} ${Math.abs(data.amount)} credits.`);
      if (data.target_type === 'admin') {
        window.dispatchEvent(new CustomEvent('admin-wallet-refresh', { detail: result }));
      }
      reset({ target_type: data.target_type, login_id: '', amount: 0, action: 'add' });
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">Add / Remove Real Credits</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900">
            ✕
          </button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Type
            </label>
            <select
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...targetTypeRegister}
            >
              <option value="user">User Real Credits</option>
              <option value="admin">Admin Wallet Balance</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {targetType === 'admin' ? 'Admin Login ID' : 'User Login ID'}
            </label>
            <input
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder={targetType === 'admin' ? 'e.g. ADM123456' : 'e.g. USR123456'}
              {...register('login_id', { required: 'Login ID is required' })}
            />
            {errors.login_id && <p className="mt-1 text-sm text-red-600">{errors.login_id.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Action
            </label>
            <select
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              {...register('action')}
            >
              <option value="add">Add Credits</option>
              <option value="remove">Remove Credits</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount
            </label>
            <input
              type="number"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter real credit amount"
              {...register('amount', {
                required: 'Amount is required',
                valueAsNumber: true,
                min: { value: 1, message: 'Amount must be at least 1' },
              })}
            />
            {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 font-medium rounded-lg text-white transition ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Updating...
              </span>
            ) : (
              'Update Real Credits'
            )}
          </button>
        </form>
      </div>

      {/* RTP Note */}
      <p className="mt-6 text-sm text-gray-500 text-center">
        User credits affect player real balance. Admin wallet balance is used by normal admins to distribute credits.
      </p>
    </div>
  );
}
